import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clienteService, prestamoService } from '../../services/api';
import '../../styles/Clientes.css';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

const Reenganche = () => {
  usePermisosGuard('prestamos', 'aprobar');
  const navigate = useNavigate();
  const { codigo } = useParams();
  const getRoute = (path) => codigo ? `/${codigo}${path}` : path;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchId, setSearchId] = useState('');
  const [prestamoOriginal, setPrestamoOriginal] = useState(null);
  const [tipoReenganche, setTipoReenganche] = useState('con_total');
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamosPorCliente, setPrestamosPorCliente] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteObj, setClienteObj] = useState(null);

  const [formData, setFormData] = useState({
    modalidad: '',
    tipo: '',
    capital_entregar: '',
    tasa: '',
    cuotas: '',
    gastos_cierre: '0',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_efectividad: new Date().toISOString().split('T')[0],
    rebajar_deuda_vieja: true  // Nueva opción: rebajar del desembolso
  });

  const [calculado, setCalculado] = useState({
    capital: 0,
    interes: 0,
    total: 0,
    cuota: 0,
    capital_entregar: 0,
    monto_base: 0,
    total_capital: 0
  });

  const tiposPrestamo = [
    { value: 'capital_final', label: 'Capital Final' },
    { value: 'capital_final_fijo', label: 'Capital Final Fijo' },
    { value: 'cuota_fija', label: 'Cuota Fija' },
    { value: 'amortizado_frances', label: 'Amortizado Frances' },
    { value: 'amortizado', label: 'Amortizado' },
    { value: 'tipo_san', label: 'Tipo San' }
  ];

  const modalidades = [
    { value: 'Diario', label: 'Diario' },
    { value: 'Semanal', label: 'Semanal' },
    { value: 'Quincenal', label: 'Quincenal' },
    { value: 'Mensual', label: 'Mensual' }
  ];

  useEffect(() => {
    loadClientes();

    // Cerrar dropdown al hacer clic fuera
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clienteService.getClientes({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  // Filtrar clientes según búsqueda
  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10);

  const seleccionarCliente = (cliente) => {
    setClienteObj(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
    handleClienteChange(cliente.id);
  };

  const limpiarCliente = () => {
    setClienteObj(null);
    setClienteSeleccionado('');
    setBusquedaCliente('');
    setPrestamoOriginal(null);
    setPrestamosPorCliente([]);
  };

  const buscarPrestamo = async () => {
    if (!searchId) {
      setError('Ingrese un ID de préstamo');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(searchId);
      const prestamo = response.data;

      if (prestamo.estado !== 'activo') {
        setError('Solo se pueden reenganchar préstamos activos');
        setPrestamoOriginal(null);
        return;
      }

      setPrestamoOriginal(prestamo);

      // Inicializar formulario con datos del préstamo original
      const montoBase = tipoReenganche === 'con_total' ? prestamo.total : prestamo.capital;

      setFormData({
        modalidad: prestamo.modalidad,
        tipo: prestamo.tipo,
        capital_entregar: montoBase.toString(),
        tasa: prestamo.tasa.toString(),
        cuotas: prestamo.cuotas.toString(),
        gastos_cierre: '0',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_efectividad: new Date().toISOString().split('T')[0]
      });

    } catch (err) {
      console.error('Error:', err);
      setError('Préstamo no encontrado');
      setPrestamoOriginal(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClienteChange = async (clienteId) => {
    setClienteSeleccionado(clienteId);
    setPrestamoOriginal(null);
    setPrestamosPorCliente([]);
    setError('');

    if (!clienteId) {
      return;
    }

    try {
      setLoading(true);
      const response = await prestamoService.getPrestamos({
        cliente_id: clienteId,
        estado: 'activo',
        per_page: 1000
      });
      const prestamos = response.data.prestamos || [];
      setPrestamosPorCliente(prestamos);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar préstamos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionarPrestamo = async (prestamoId) => {
    if (!prestamoId || prestamoId === 'null' || prestamoId === 'undefined') {
      setError('ID de préstamo inválido. Por favor, seleccione un préstamo de la lista.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(prestamoId);
      const prestamo = response.data;

      if (prestamo.estado !== 'activo') {
        setError('Solo se pueden reenganchar préstamos activos');
        setPrestamoOriginal(null);
        return;
      }

      setPrestamoOriginal(prestamo);

      // Inicializar formulario con datos del préstamo original
      // Capital a entregar inicia en 0 - el usuario lo ingresa
      setFormData({
        modalidad: prestamo.modalidad,
        tipo: prestamo.tipo,
        capital_entregar: '0',
        tasa: prestamo.tasa.toString(),
        cuotas: prestamo.cuotas.toString(),
        gastos_cierre: '0',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_efectividad: new Date().toISOString().split('T')[0],
        rebajar_deuda_vieja: true  // Por defecto, rebajar la deuda del desembolso
      });

    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const calcularPrestamo = useCallback(async () => {
    if (!formData.capital_entregar || !formData.cuotas || !prestamoOriginal) return;

    try {
      // Calcular saldo pendiente si no viene del backend
      let saldoPendiente = prestamoOriginal.saldo_pendiente;
      
      // Si no viene saldo_pendiente, calcularlo: total - total_pagado
      if (saldoPendiente === undefined || saldoPendiente === null) {
        const total = parseFloat(prestamoOriginal.total || prestamoOriginal.monto_prestado || 0);
        const totalPagado = parseFloat(prestamoOriginal.total_pagado || 0);
        saldoPendiente = Math.max(0, total - totalPagado);
        console.log('⚠️ saldo_pendiente calculado en frontend:', {
          total,
          totalPagado,
          saldoPendiente
        });
      }
      
      // Calcular monto base según tipo de reenganche
      // IMPORTANTE: Si es 'con_total', usar el SALDO PENDIENTE (lo que realmente debe), NO el total
      const montoBase = tipoReenganche === 'con_total' ?
        saldoPendiente :  // ✅ CORRECTO: Usar saldo pendiente (deuda real)
        parseFloat(prestamoOriginal.capital || prestamoOriginal.monto_prestado || 0);
      
      console.log('📊 Cálculo de reenganche:', {
        tipoReenganche,
        saldoPendiente,
        capital: prestamoOriginal.capital,
        montoBase,
        capitalSolicitado: formData.capital_entregar
      });

      const capitalSolicitado = parseFloat(formData.capital_entregar) || 0;
      const gastosCierre = parseFloat(formData.gastos_cierre) || 0;
      
      // Nueva lógica: Rebajar o no la deuda vieja
      let totalCapital;
      let capitalADesembolsar;
      
      if (formData.rebajar_deuda_vieja) {
        // OPCIÓN 1: REBAJAR LA DEUDA DEL DESEMBOLSO
        // Préstamo nuevo = Capital solicitado
        // Desembolso = Capital solicitado - Deuda vieja - Gastos de cierre
        totalCapital = capitalSolicitado;
        capitalADesembolsar = capitalSolicitado - montoBase - gastosCierre;
      } else {
        // OPCIÓN 2: NO REBAJAR (SUMAR LA DEUDA AL PRÉSTAMO)
        // Préstamo nuevo = Deuda vieja + Capital solicitado
        // Desembolso = Capital solicitado - Gastos de cierre
        totalCapital = montoBase + capitalSolicitado;
        capitalADesembolsar = capitalSolicitado - gastosCierre;
      }

      const response = await prestamoService.calcularPrestamo({
        monto: totalCapital,
        tasa: parseFloat(formData.tasa) || 0,
        cuotas: parseInt(formData.cuotas) || 1,
        tipo: formData.tipo,
        gastos_cierre: parseFloat(formData.gastos_cierre) || 0
      });

      setCalculado({
        capital: response.data.capital,
        interes: response.data.interes,
        total: response.data.total,
        cuota: response.data.cuota_semanal,
        capital_entregar: capitalADesembolsar,  // Lo que realmente se le entrega
        capital_solicitado: capitalSolicitado,  // Lo que pidió
        monto_base: montoBase,
        total_capital: totalCapital,
        gastos_cierre: gastosCierre
      });
    } catch (err) {
      console.error('Error al calcular:', err);
    }
  }, [formData.capital_entregar, formData.tasa, formData.cuotas, formData.tipo, formData.gastos_cierre, formData.rebajar_deuda_vieja, prestamoOriginal, tipoReenganche]);

  useEffect(() => {
    if (formData.capital_entregar && formData.cuotas && prestamoOriginal) {
      calcularPrestamo();
    }
  }, [formData.capital_entregar, formData.tasa, formData.cuotas, formData.tipo, formData.gastos_cierre, tipoReenganche, calcularPrestamo]);

  // Recalcular cuando cambia el tipo de reenganche
  useEffect(() => {
    if (prestamoOriginal) {
      calcularPrestamo();
    }
  }, [tipoReenganche, prestamoOriginal, calcularPrestamo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prestamoOriginal) {
      setError('Debe buscar un préstamo primero');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Calcular saldo pendiente si no viene del backend (misma lógica que en calcularPrestamo)
      let saldoPendiente = prestamoOriginal.saldo_pendiente;
      if (saldoPendiente === undefined || saldoPendiente === null) {
        const total = parseFloat(prestamoOriginal.total || prestamoOriginal.monto_prestado || 0);
        const totalPagado = parseFloat(prestamoOriginal.total_pagado || 0);
        saldoPendiente = Math.max(0, total - totalPagado);
      }
      
      // Calcular monto base según tipo de reenganche
      // IMPORTANTE: Si es 'con_total', usar el SALDO PENDIENTE (lo que realmente debe), NO el total
      const montoBase = tipoReenganche === 'con_total' ?
        saldoPendiente :  // ✅ CORRECTO: Usar saldo pendiente (deuda real)
        parseFloat(prestamoOriginal.capital || prestamoOriginal.monto_prestado || 0);

      const capitalSolicitado = parseFloat(formData.capital_entregar) || 0;
      
      // Nueva lógica: Rebajar o no la deuda vieja
      let montoTotal;
      let capitalADesembolsar;
      
      if (formData.rebajar_deuda_vieja) {
        // OPCIÓN 1: REBAJAR - Préstamo = capital solicitado, Desembolso = solicitado - deuda
        montoTotal = capitalSolicitado;
        capitalADesembolsar = capitalSolicitado - montoBase;
      } else {
        // OPCIÓN 2: NO REBAJAR - Préstamo = deuda + solicitado, Desembolso = solicitado
        montoTotal = montoBase + capitalSolicitado;
        capitalADesembolsar = capitalSolicitado;
      }

      const reengancheData = {
        monto: montoTotal,
        capital_entregar: capitalADesembolsar,  // Lo que realmente se desembolsa
        capital_solicitado: capitalSolicitado,  // Lo que pidió el cliente
        monto_base: montoBase,
        tipo_reenganche: tipoReenganche,
        rebajar_deuda_vieja: formData.rebajar_deuda_vieja,  // Nueva bandera
        tasa: parseFloat(formData.tasa),
        cuotas: parseInt(formData.cuotas),
        modalidad: formData.modalidad,
        tipo: formData.tipo,
        fecha_inicio: formData.fecha_inicio,
        fecha_efectividad: formData.fecha_efectividad,
        fecha_primer_pago: formData.fecha_efectividad,
        gastos_cierre: parseFloat(formData.gastos_cierre) || 0,
        ruta_id: prestamoOriginal.ruta_id,
        ruta: prestamoOriginal.ruta,
        negocio: prestamoOriginal.negocio,
        direccion: prestamoOriginal.direccion,
        tiene_mora: prestamoOriginal.tiene_mora,
        mora_diaria: prestamoOriginal.mora_diaria
      };

      const response = await prestamoService.crearReenganche(prestamoOriginal.id, reengancheData);

      // Mostrar notificación estilo EasyPres
      setSuccess('¡Reenganche guardado exitosamente!');

      // Esperar 2 segundos antes de redirigir
      setTimeout(() => {
        navigate(getRoute('/prestamos/lista'));
      }, 2000);

    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al crear el reenganche');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Reenganche</h1>
        <button
          className="btn"
          onClick={() => navigate(getRoute('/prestamos'))}
          style={{background: '#666'}}
        >
          ← Volver
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}

      {/* Modal de éxito estilo EasyPres */}
      {success && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#4caf50',
          color: 'white',
          padding: '1.5rem 2rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            ✓
          </div>
          <div>
            <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '4px'}}>
              Nuevo Reenganche
            </div>
            <div style={{fontSize: '14px'}}>
              El préstamo se ha guardado con éxito.
            </div>
          </div>
        </div>
      )}

      {/* Búsqueda */}
      <div className="card" style={{marginBottom: '2rem'}}>
        <div className="card-title" style={{background: '#3498db'}}>
          Buscar Préstamo
        </div>
        <div style={{padding: '1rem'}}>
          {/* Búsqueda por ID */}
          <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem'}}>
            <div style={{flex: 1}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Préstamo ID
              </label>
              <input
                type="number"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                placeholder="Ingrese el ID del préstamo"
                className="form-control"
              />
            </div>
            <button
              onClick={buscarPrestamo}
              className="btn"
              style={{background: '#3498db', color: '#fff', padding: '0.75rem 2rem'}}
              disabled={loading}
            >
              Buscar por ID
            </button>
          </div>

          {/* Separador */}
          <div style={{
            borderTop: '2px dashed #ddd',
            margin: '1.5rem 0',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              padding: '0 1rem',
              color: '#666',
              fontWeight: 600
            }}>
              O
            </span>
          </div>

          {/* Búsqueda por Cliente */}
          <div style={{ position: 'relative' }}>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
              Buscar por Cliente
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Buscar por nombre, cédula o apodo..."
                value={busquedaCliente}
                onChange={(e) => {
                  setBusquedaCliente(e.target.value);
                  setMostrarDropdown(true);
                  if (!e.target.value) {
                    limpiarCliente();
                  }
                }}
                onFocus={() => setMostrarDropdown(true)}
                className="form-control"
                style={{
                  paddingRight: clienteObj ? '40px' : '10px'
                }}
              />
              {clienteObj && (
                <button
                  type="button"
                  onClick={limpiarCliente}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Dropdown de resultados */}
            {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '300px',
                overflowY: 'auto',
                backgroundColor: 'white',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 1000,
                marginTop: '4px'
              }}>
                {clientesFiltrados.map(cliente => (
                  <div
                    key={cliente.id}
                    onClick={() => seleccionarCliente(cliente)}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    {/* Foto del cliente */}
                    {cliente.foto_url ? (
                      <img
                        src={cliente.foto_url}
                        alt={cliente.nombres}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                      </div>
                    )}

                    {/* Información del cliente */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {cliente.nombres} {cliente.apellidos}
                      </div>
                      {cliente.apodo && (
                        <div style={{ fontSize: '0.85rem', color: '#2196F3', fontStyle: 'italic' }}>
                          "{cliente.apodo}"
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Cédula: {cliente.cedula || 'N/A'} | ID: {cliente.id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mensaje cuando no hay resultados */}
            {mostrarDropdown && busquedaCliente && clientesFiltrados.length === 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid #ddd',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '4px',
                textAlign: 'center',
                color: '#666',
                zIndex: 1000
              }}>
                No se encontraron clientes
              </div>
            )}
          </div>

          {/* Lista de préstamos del cliente */}
          {prestamosPorCliente.length > 0 && (
            <div style={{marginTop: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Préstamos Activos del Cliente
              </label>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                {prestamosPorCliente.map(prestamo => {
                  // Calcular saldo pendiente
                  const total = parseFloat(prestamo.total || prestamo.monto_prestado || 0);
                  const totalPagado = parseFloat(prestamo.total_pagado || 0);
                  const saldoPendiente = Math.max(0, total - totalPagado);
                  
                  return (
                    <div
                      key={prestamo.id}
                      onClick={() => handleSeleccionarPrestamo(prestamo.id)}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        background: prestamoOriginal?.id === prestamo.id ? '#e3f2fd' : '#fff',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (prestamoOriginal?.id !== prestamo.id) {
                          e.currentTarget.style.background = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (prestamoOriginal?.id !== prestamo.id) {
                          e.currentTarget.style.background = '#fff';
                        }
                      }}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div>
                          <strong>ID: {prestamo.id}</strong> - {prestamo.modalidad}
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div style={{fontSize: '0.85rem', color: '#666', marginBottom: '2px'}}>
                            Total: ${total.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                          </div>
                          <div style={{fontWeight: 'bold', color: '#e67e22', fontSize: '1.1rem'}}>
                            Pendiente: ${saldoPendiente.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                          </div>
                          <span className="badge badge-success" style={{marginTop: '4px'}}>
                            {prestamo.estado}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {clienteSeleccionado && prestamosPorCliente.length === 0 && !loading && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#856404'
            }}>
              Este cliente no tiene préstamos activos para reenganchar
            </div>
          )}
        </div>
      </div>

      {/* Información del Préstamo Original */}
      {prestamoOriginal && (
        <>
          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#27ae60'}}>
              Información del Préstamo Original
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Cliente</label>
                <div style={{fontWeight: 'bold'}}>
                  {prestamoOriginal.cliente?.nombres} {prestamoOriginal.cliente?.apellidos}
                </div>
              </div>

              <div className="form-group">
                <label>ID</label>
                <div>{prestamoOriginal.id}</div>
              </div>

              <div className="form-group">
                <label>Capital Original</label>
                <div>{formatMoney(prestamoOriginal.capital)}</div>
              </div>

              <div className="form-group">
                <label>Total Original</label>
                <div style={{color: '#666'}}>
                  {formatMoney(prestamoOriginal.total)}
                </div>
              </div>

              <div className="form-group">
                <label>Total Pagado</label>
                <div style={{color: '#27ae60', fontWeight: 'bold'}}>
                  {formatMoney(prestamoOriginal.total_pagado || 0)}
                </div>
              </div>

              <div className="form-group">
                <label style={{fontSize: '1.1rem'}}>💰 TOTAL PENDIENTE</label>
                <div style={{fontWeight: 'bold', color: '#e67e22', fontSize: '1.3rem'}}>
                  {formatMoney(
                    Math.max(0, 
                      parseFloat(prestamoOriginal.total || prestamoOriginal.monto_prestado || 0) - 
                      parseFloat(prestamoOriginal.total_pagado || 0)
                    )
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Modalidad</label>
                <div>{prestamoOriginal.modalidad}</div>
              </div>

              <div className="form-group">
                <label>Tipo</label>
                <div>{prestamoOriginal.tipo}</div>
              </div>
            </div>
          </div>

          {/* Tipo de Reenganche */}
          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#3498db'}}>
              Asientos - Tipo de Reenganche
            </div>
            <div style={{padding: '1rem'}}>
              <div style={{display: 'flex', gap: '2rem'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                  <input
                    type="radio"
                    value="con_total"
                    checked={tipoReenganche === 'con_total'}
                    onChange={(e) => setTipoReenganche(e.target.value)}
                  />
                  <span>Con el total</span>
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                  <input
                    type="radio"
                    value="con_capital"
                    checked={tipoReenganche === 'con_capital'}
                    onChange={(e) => setTipoReenganche(e.target.value)}
                  />
                  <span>Con el capital</span>
                </label>
              </div>
            </div>
          </div>

          {/* Formulario del Reenganche */}
          <form onSubmit={handleSubmit}>
            <div className="card" style={{marginBottom: '2rem'}}>
              <div className="card-title" style={{background: '#27ae60'}}>
                Datos del Reenganche
              </div>

              {/* Nueva opción: Rebajar deuda vieja */}
              <div style={{padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem'}}>
                <label style={{fontWeight: 'bold', marginBottom: '0.5rem', display: 'block', color: '#2c3e50'}}>
                  💰 ¿Rebajar total del préstamo viejo del desembolso?
                </label>
                <div style={{display: 'flex', gap: '2rem', marginTop: '0.5rem'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                    <input
                      type="radio"
                      name="rebajar_deuda_vieja"
                      checked={formData.rebajar_deuda_vieja === true}
                      onChange={() => setFormData(prev => ({...prev, rebajar_deuda_vieja: true}))}
                      style={{width: '18px', height: '18px'}}
                    />
                    <span style={{fontSize: '1rem'}}>
                      ✅ <strong>SÍ</strong> - Rebajar del desembolso
                    </span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                    <input
                      type="radio"
                      name="rebajar_deuda_vieja"
                      checked={formData.rebajar_deuda_vieja === false}
                      onChange={() => setFormData(prev => ({...prev, rebajar_deuda_vieja: false}))}
                      style={{width: '18px', height: '18px'}}
                    />
                    <span style={{fontSize: '1rem'}}>
                      ❌ <strong>NO</strong> - Sumar al préstamo nuevo
                    </span>
                  </label>
                </div>
                <small style={{display: 'block', marginTop: '0.5rem', color: '#666', fontStyle: 'italic'}}>
                  {formData.rebajar_deuda_vieja 
                    ? '📤 Se restará la deuda vieja del monto a entregar al cliente'
                    : '📈 La deuda vieja se sumará al nuevo préstamo (cliente recibe el monto completo)'}
                </small>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Capital solicitado por el cliente *</label>
                  <input
                    type="number"
                    name="capital_entregar"
                    value={formData.capital_entregar}
                    onChange={handleChange}
                    step="0.01"
                    required
                    className="form-control"
                    placeholder="Ej: 50000"
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                    💵 Monto que el cliente está pidiendo
                  </small>
                </div>

                <div className="form-group">
                  <label>Tasa *</label>
                  <input
                    type="number"
                    name="tasa"
                    value={formData.tasa}
                    onChange={handleChange}
                    step="0.01"
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Modalidad *</label>
                  <select
                    name="modalidad"
                    value={formData.modalidad}
                    onChange={handleChange}
                    required
                    className="form-control"
                  >
                    {modalidades.map(mod => (
                      <option key={mod.value} value={mod.value}>
                        {mod.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Tipo Préstamo *</label>
                  <select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                    className="form-control"
                  >
                    {tiposPrestamo.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Cantidad de pagos *</label>
                  <input
                    type="number"
                    name="cuotas"
                    value={formData.cuotas}
                    onChange={handleChange}
                    min="1"
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Gastos de cierre</label>
                  <input
                    type="number"
                    name="gastos_cierre"
                    value={formData.gastos_cierre}
                    onChange={handleChange}
                    step="0.01"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>FECHA DE DESEMBOLSO *</label>
                  <input
                    type="date"
                    name="fecha_inicio"
                    value={formData.fecha_inicio}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                    📤 Fecha en que se entrega el dinero al cliente
                  </small>
                </div>

                <div className="form-group">
                  <label>FECHA DEL PRIMER PAGO *</label>
                  <input
                    type="date"
                    name="fecha_efectividad"
                    value={formData.fecha_efectividad}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                    📥 Fecha en que el cliente debe pagar su primera cuota
                  </small>
                </div>
              </div>
            </div>

            {/* Resultados */}
            <div className="card" style={{marginBottom: '2rem'}}>
              <div className="card-title" style={{background: '#3498db'}}>
                Cálculos del Reenganche
              </div>

              {/* Visualización del cálculo */}
              {calculado.monto_base > 0 && calculado.capital_solicitado > 0 && (
                <div style={{padding: '1rem', background: '#e8f5e9', borderRadius: '8px', marginBottom: '1rem', border: '2px solid #4caf50'}}>
                  <h4 style={{marginBottom: '1rem', color: '#2e7d32'}}>📊 Desglose del Cálculo:</h4>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'white', borderRadius: '4px'}}>
                      <span>💸 Deuda del préstamo viejo:</span>
                      <strong style={{color: '#e74c3c'}}>${calculado.monto_base.toFixed(2)}</strong>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'white', borderRadius: '4px'}}>
                      <span>💰 Capital solicitado:</span>
                      <strong style={{color: '#3498db'}}>${calculado.capital_solicitado.toFixed(2)}</strong>
                    </div>
                    {calculado.gastos_cierre > 0 && (
                      <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#ffe5e5', borderRadius: '4px'}}>
                        <span>💳 Gastos de cierre:</span>
                        <strong style={{color: '#c0392b'}}>- ${calculado.gastos_cierre.toFixed(2)}</strong>
                      </div>
                    )}
                    <hr style={{margin: '0.5rem 0', border: '1px dashed #ccc'}} />
                    <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'white', borderRadius: '4px'}}>
                      <span><strong>📋 Préstamo nuevo (capital):</strong></span>
                      <strong style={{color: '#27ae60', fontSize: '1.1rem'}}>${calculado.total_capital.toFixed(2)}</strong>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px', border: '2px solid #ffc107'}}>
                      <span><strong>📤 A DESEMBOLSAR AL CLIENTE:</strong></span>
                      <strong style={{color: '#f39c12', fontSize: '1.2rem'}}>${calculado.capital_entregar.toFixed(2)}</strong>
                    </div>
                  </div>
                  <small style={{display: 'block', marginTop: '0.5rem', color: '#666', fontStyle: 'italic', textAlign: 'center'}}>
                    {formData.rebajar_deuda_vieja 
                      ? (calculado.gastos_cierre > 0 
                          ? `✅ Fórmula: Desembolso = $${calculado.capital_solicitado.toFixed(2)} - $${calculado.monto_base.toFixed(2)} - $${calculado.gastos_cierre.toFixed(2)} = $${calculado.capital_entregar.toFixed(2)}`
                          : `✅ Fórmula: Desembolso = $${calculado.capital_solicitado.toFixed(2)} - $${calculado.monto_base.toFixed(2)} = $${calculado.capital_entregar.toFixed(2)}`)
                      : (calculado.gastos_cierre > 0
                          ? `✅ Fórmula: Préstamo = $${calculado.monto_base.toFixed(2)} + $${calculado.capital_solicitado.toFixed(2)} = $${calculado.total_capital.toFixed(2)}, Desembolso = $${calculado.capital_solicitado.toFixed(2)} - $${calculado.gastos_cierre.toFixed(2)} = $${calculado.capital_entregar.toFixed(2)}`
                          : `✅ Fórmula: Préstamo = $${calculado.monto_base.toFixed(2)} + $${calculado.capital_solicitado.toFixed(2)} = $${calculado.total_capital.toFixed(2)}, Desembolso = $${calculado.capital_entregar.toFixed(2)}`)}
                  </small>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Total Capital (Préstamo Nuevo)</label>
                  <input
                    type="text"
                    value={formatMoney(calculado.total_capital || 0)}
                    disabled
                    className="form-control"
                    style={{background: '#fff3cd', fontWeight: 'bold'}}
                  />
                </div>

                <div className="form-group">
                  <label>Interés</label>
                  <input
                    type="text"
                    value={formatMoney(calculado.interes)}
                    disabled
                    className="form-control"
                    style={{background: '#f5f5f5'}}
                  />
                </div>

                <div className="form-group">
                  <label>Total</label>
                  <input
                    type="text"
                    value={formatMoney(calculado.total)}
                    disabled
                    className="form-control"
                    style={{background: '#f5f5f5'}}
                  />
                </div>

                <div className="form-group">
                  <label>Cuota</label>
                  <input
                    type="text"
                    value={formatMoney(calculado.cuota)}
                    disabled
                    className="form-control"
                    style={{background: '#f5f5f5'}}
                  />
                </div>

                {/* Campo destacado: Capital a Desembolsar */}
                <div className="form-group" style={{gridColumn: '1 / -1'}}>
                  <label style={{fontSize: '1.1rem', fontWeight: 'bold', color: '#f39c12'}}>
                    📤 CAPITAL A DESEMBOLSAR (Dinero que se le entrega al cliente)
                  </label>
                  <input
                    type="text"
                    value={formatMoney(calculado.capital_entregar || 0)}
                    disabled
                    className="form-control"
                    style={{
                      background: '#fff3cd', 
                      fontWeight: 'bold', 
                      fontSize: '1.3rem',
                      color: '#f39c12',
                      border: '2px solid #ffc107',
                      textAlign: 'center'
                    }}
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block', textAlign: 'center'}}>
                    {formData.rebajar_deuda_vieja 
                      ? `💡 Cálculo: $${calculado.capital_solicitado?.toFixed(2)} (solicitado) - $${calculado.monto_base.toFixed(2)} (deuda vieja) = $${calculado.capital_entregar.toFixed(2)}`
                      : `💡 El cliente recibe el monto completo solicitado: $${calculado.capital_entregar.toFixed(2)}`}
                  </small>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
              <button
                type="button"
                onClick={() => navigate(getRoute('/prestamos'))}
                className="btn"
                style={{background: '#95a5a6'}}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn"
                style={{background: '#27ae60', color: '#fff'}}
                disabled={loading}
              >
                {loading ? 'Creando...' : 'Guardar Reenganche'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Reenganche;
